<template>
  <div class="full-container" v-if="comments.length || remainingReplies">
    <!-- List -->
    <div class="list-container" v-if="showReplies">
      <CommentSingle
        v-for="comment in comments"
        :key="comment.id"
        :nested="true"
        :post="post"
        :model="model"
        :data="comment"
        @delete="deleteCommentHandler"
        @reply="raiseReply"
      />
    </div>

    <!-- Error -->
    <div v-if="errorFetching">
      <p>
        {{ $t("error") }}
      </p>
      <button style="width: 100%" type="button" @click="loadComments">
        {{ $t("retry") }}
      </button>
    </div>
    <!-- Loading -->
    <div class="spinner" v-else-if="waitFetch">
      <Spinner />
    </div>

    <div 
      v-else-if="remainingReplies > 0"
      class="show-more-replies-container"
      @click="loadComments"
    >
      <div class="replies-decorator"></div>
      {{ $tc("comments.placeholder.see_nth_responses", remainingReplies, { n: $n(remainingReplies) }) }}
    </div>
  </div>
</template>

<script>
import resources from "../../resources";
import Spinner from "../Spinner.vue";

export default {
  components: {
    CommentSingle: () => import("./Single.vue"),
    Spinner,
  },
  props: {
    post: {
      type: Object,
      required: true,
    },
    model: {
      type: Object,
      required: true,
    },
    commentId: {
      type: Number,
      required: true,
    },
    replies: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      comments: [],
      errorFetching: false,
      waitFetch: false,
      nextPage: null,
      remainingReplies: this.replies,
      showReplies: true,
      excludedComments: [],
    };
  },
  methods: {
    deleteCommentHandler(commentId) {
      const filterComment = (commentList) => {
        return commentList.filter((comment) => comment.id !== commentId);
      };
      
      this.excludedComments = filterComment(this.excludedComments);
      
      const oldLength = this.comments.length;
      this.comments = filterComment(this.comments);
      
      if (oldLength !== this.comments.length) this.$emit("delete");
    },
    appendComment(comment) {
      this.excludedComments.push(comment.id);
      this.comments.push(comment);
      this.raiseChangeHeight();
    },
    loadComments() {
      if (this.waitFetch) return;

      if (this.remainingReplies <= 0) {
        this.showReplies = false;
        this.remainingReplies = this.replies + this.excludedComments.length;
        return;
      }

      if (!this.showReplies) {
        this.showReplies = true;
        this.remainingReplies = 0;
        return;
      }

      this.waitFetch = true;
      this.errorFetching = false;

      let fetchRequest = null;

      if (this.nextPage) {
        fetchRequest = resources.comments.getCommentsByPaginatorLink(
          this.nextPage
        );
      } else {
        fetchRequest = resources.comments.getReplies(
          this.post.post_id,
          this.commentId
        );
      }

      this.nextPage = null;

      fetchRequest
        .then((response) => {
          let data = response.data.data;
          const meta = response.data.meta;

          const oldLength = data.length;
          if (this.excludedComments.length) {
            data = data.filter(
              (comment) => !this.excludedComments.includes(comment.id)
            );
          }

          this.comments = this.comments.concat(data);
          this.raiseChangeHeight();

          if (!meta || !meta.pagination) return;
          const pagination = meta.pagination;

          if (
            pagination.current_page < meta.pagination.total_pages &&
            meta.pagination.links
          ) {
            this.nextPage = meta.pagination.links.next;
          }

          this.remainingReplies -= meta.pagination.count - (oldLength - data.length);
        })
        .catch((error) => {
          console.log(error);
          this.errorFetching = true;
        })
        .finally(() => {
          this.waitFetch = false;
        });
    },
    raiseReply(reply) {
      this.$emit("reply", reply);
    },
    raiseChangeHeight() {
      this.$nextTick(() => {
        this.$emit("changeHeight");
      });
    }
  },
};
</script>

<style scoped lang="scss">
@import "../../styles/variables.scss";

.show-more-replies-container {
  display: flex;
  flex-direction: row;
  width: 100%;
  font-size: 0.9em;
  font-weight: 500;

  $COLOR: var(--pallete_dark_text_color);
  color: $COLOR;

  -webkit-tap-highlight-color: transparent;
  background: none;

  &:hover {
    cursor: pointer;
  }

  > .replies-decorator {
    height: 1px;
    width: 2.5em;
    border-top: 1px solid $COLOR;
    margin-block: auto;
    margin-right: 0.5em;
  }
}

.full-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
}

.list-container {
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  margin: 0;
  padding: 0;
}

.spinner {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}
</style>
